import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { StartClient } from "@tanstack/react-start/client";

import { DEFAULT_LOCALE, type Locale } from "./i18n/locales";
import { detectLocale } from "./i18n/runtime";
import { registerMessages, type Bundle } from "./i18n/messages";
import { registerSeoContent, type SeoContentPack } from "./lib/seo-content";

/**
 * One chunk per language, so a mobile visitor downloads only the language they
 * are reading instead of all eight. Loaded before hydration: the SSR markup is
 * already translated, so the client must have the same strings on first render.
 */
const bundles = import.meta.glob<{ default: Bundle }>("./i18n/bundles/*.ts");
const seoPacks = import.meta.glob<{ default: SeoContentPack }>("./lib/seo-content/*.ts");

async function loadLocale(locale: Locale) {
  const loader = bundles[`./i18n/bundles/${locale}.ts`];
  const seoLoader = seoPacks[`./lib/seo-content/${locale}.ts`];
  await Promise.all([
    (async () => {
      if (!loader) return;
      try {
        registerMessages(locale, (await loader()).default);
      } catch {
        // A failed dictionary chunk must never block the app: core strings are
        // already bundled and the server-rendered markup stays on screen.
      }
    })(),
    (async () => {
      if (!seoLoader) return;
      try {
        registerSeoContent(locale, (await seoLoader()).default);
      } catch {
        // English SEO copy is the fallback; the rendered page stays intact.
      }
    })(),
  ]);
}

async function bootstrap() {
  const locale = detectLocale();
  await Promise.all(
    locale === DEFAULT_LOCALE
      ? [loadLocale(DEFAULT_LOCALE)]
      : [loadLocale(locale), loadLocale(DEFAULT_LOCALE)],
  );

  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <StartClient />
      </StrictMode>,
    );
  });
}

void bootstrap();
